/**
 * Stacked markers (hotspot / portal `type: 'stack'`): the pure parts.
 *
 * A stack is one marker made of several media layers (image / video / GIF) drawn top to
 * bottom, e.g. a looping video sign with a PNG title label just under it. The viewer builds
 * one root entity (the marker: position, rotation, scale, billboard, opacity, visibility,
 * picking) and one plane child per layer; this module decides which layers are usable and
 * where each child goes. Nothing here touches PlayCanvas.
 *
 * Units: "marker units". 1 is the width of an image marker at scale 1; the marker's own
 * scale multiplies everything. +x is right, +y is up. The layout is centred vertically on the
 * marker's position, so the marker's pivot (gizmo, rotation, billboard, proximity) is the
 * middle of the stack. Horizontally every layer is centred on x = 0 plus its own offsetX.
 */
import type { MarkerLayer, MarkerLayerKind } from '../types';
import { webmHasAlphaWithoutName } from '../utils/videoFormat';

export const MARKER_LAYER_KINDS: readonly MarkerLayerKind[] = ['image', 'video', 'gif'];
/** Default layer width, in marker units. */
export const MARKER_LAYER_DEFAULT_WIDTH = 1;
export const MARKER_LAYER_MIN_WIDTH = 0.01;
export const MARKER_LAYER_MAX_WIDTH = 100;
export const MARKER_LAYER_MAX_OFFSET = 100;
/** Hard cap on layers, so a corrupt scene cannot create thousands of planes. */
export const MARKER_STACK_MAX_LAYERS = 16;
/** Only one layer can be the marker's video (`entity.videoElement`); later video layers are ignored. */
export const MARKER_STACK_MAX_VIDEO_LAYERS = 1;
/**
 * Distance between consecutive layers along the marker's normal, in marker units. Later
 * (lower) layers sit in front, so a label that overlaps the video above it is drawn over it.
 */
export const MARKER_STACK_LAYER_DEPTH = 0.004;
/** Aspect (width / height) assumed until a layer's media reports its size. */
export const MARKER_LAYER_PROVISIONAL_ASPECT: Record<MarkerLayerKind, number> = { image: 1, gif: 1, video: 16 / 9 };

/** Any shape a stored layer might arrive in: fields can be missing, null or the wrong type. */
export type MarkerLayerInput = { [K in keyof MarkerLayer]?: MarkerLayer[K] | null };

const VIDEO_URL_FIELDS = ['videoBackupUrl', 'iosMainVideoUrl', 'alphaMaskVideoUrl'] as const;
const VIDEO_NAME_FIELDS = ['videoBackupName', 'iosMainVideoName', 'alphaMaskVideoName'] as const;
const VIDEO_FLAG_FIELDS = ['webmHasAlpha', 'useIOSVideoAlphaMethod', 'forceIOSVideoAlphaMethodForAllDevices'] as const;

function isKind(value: string | null | undefined): value is MarkerLayerKind {
    return typeof value === 'string' && (MARKER_LAYER_KINDS as readonly string[]).includes(value);
}

function finiteOr(value: number | null | undefined, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function nonEmptyString(value: string | null | undefined): string | undefined {
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * The layers a viewer can draw, in order. Drops anything that is not an object, has an
 * unknown kind or has no URL; fills in ids; clamps width and offset; keeps only the first
 * video layer; caps the count. Never throws, whatever the scene JSON holds.
 */
export function sanitizeMarkerStack(
    layers: ReadonlyArray<MarkerLayerInput | null | undefined> | null | undefined,
): MarkerLayer[] {
    if (!Array.isArray(layers)) return [];
    const out: MarkerLayer[] = [];
    let videos = 0;
    for (let index = 0; index < layers.length && out.length < MARKER_STACK_MAX_LAYERS; index++) {
        const raw = layers[index];
        if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
        if (!isKind(raw.kind)) continue;
        const url = nonEmptyString(raw.url);
        if (!url) continue;
        if (raw.kind === 'video') {
            if (videos >= MARKER_STACK_MAX_VIDEO_LAYERS) continue;
            videos++;
        }
        const width = finiteOr(raw.width, MARKER_LAYER_DEFAULT_WIDTH);
        const layer: MarkerLayer = {
            id: nonEmptyString(raw.id) ?? `layer-${index}`,
            kind: raw.kind,
            url,
            width: width > 0 ? clamp(width, MARKER_LAYER_MIN_WIDTH, MARKER_LAYER_MAX_WIDTH) : MARKER_LAYER_DEFAULT_WIDTH,
            offsetX: clamp(finiteOr(raw.offsetX, 0), -MARKER_LAYER_MAX_OFFSET, MARKER_LAYER_MAX_OFFSET),
        };
        const name = nonEmptyString(raw.name);
        if (name) layer.name = name;
        if (raw.kind === 'video') {
            for (const field of VIDEO_URL_FIELDS) {
                const value = nonEmptyString(raw[field]);
                if (value) layer[field] = value;
            }
            for (const field of VIDEO_NAME_FIELDS) {
                const value = nonEmptyString(raw[field]);
                if (value) layer[field] = value;
            }
            for (const field of VIDEO_FLAG_FIELDS) {
                if (typeof raw[field] === 'boolean') layer[field] = raw[field] as boolean;
            }
        }
        out.push(layer);
    }
    return out;
}

/** True when a stack has at least one layer the viewer can draw. */
export function hasRenderableMarkerStack(layers: ReadonlyArray<MarkerLayerInput | null | undefined> | null | undefined): boolean {
    return sanitizeMarkerStack(layers).length > 0;
}

/** True when the stack's drawable layers include a video (the marker then owns a video element). */
export function markerStackHasVideo(layers: ReadonlyArray<MarkerLayerInput | null | undefined> | null | undefined): boolean {
    return sanitizeMarkerStack(layers).some((layer) => layer.kind === 'video');
}

/** The stack gap a viewer uses: finite, clamped to a sane range, default 0. */
export function normalizeMarkerStackSpacing(spacing: number | null | undefined): number {
    return clamp(finiteOr(spacing, 0), -MARKER_LAYER_MAX_WIDTH, MARKER_LAYER_MAX_WIDTH);
}

export interface MarkerStackLayoutLayer {
    /** Layer width in marker units. */
    width: number;
    /** Media aspect ratio, width / height. Non-finite or non-positive values count as 1. */
    aspect: number;
    /** Horizontal offset in marker units. */
    offsetX?: number;
}

/** Where one layer plane goes inside the marker, in marker units (+y up). */
export interface MarkerStackLayerPlacement {
    /** Centre of the layer. */
    x: number;
    y: number;
    width: number;
    height: number;
    /** Offset along the marker normal; later layers are in front. */
    depth: number;
}

export interface MarkerStackBounds {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    width: number;
    height: number;
}

export interface MarkerStackLayout {
    layers: MarkerStackLayerPlacement[];
    bounds: MarkerStackBounds;
}

/**
 * Stack layers top to bottom. Each layer is `width` wide and `width / aspect` tall, centred
 * on x = offsetX. Consecutive layers are `spacing` apart (negative overlaps them). A gap can
 * overlap at most the shorter of its two layers, so a layer never rises above the one before
 * it. The result is centred vertically: bounds.minY = -bounds.maxY.
 */
export function layoutMarkerStack(layers: readonly MarkerStackLayoutLayer[], spacing = 0): MarkerStackLayout {
    if (layers.length === 0) {
        return { layers: [], bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 } };
    }
    const gap = normalizeMarkerStackSpacing(spacing);
    const sized = layers.map((layer) => {
        const width = clamp(finiteOr(layer.width, MARKER_LAYER_DEFAULT_WIDTH), MARKER_LAYER_MIN_WIDTH, MARKER_LAYER_MAX_WIDTH);
        const rawAspect = finiteOr(layer.aspect, 1);
        const aspect = rawAspect > 0 ? clamp(rawAspect, 0.01, 100) : 1;
        return { width, height: width / aspect, offsetX: clamp(finiteOr(layer.offsetX, 0), -MARKER_LAYER_MAX_OFFSET, MARKER_LAYER_MAX_OFFSET) };
    });
    const placements: MarkerStackLayerPlacement[] = [];
    let top = 0;
    sized.forEach((layer, index) => {
        if (index > 0) {
            const previous = sized[index - 1];
            top -= previous.height + Math.max(gap, -Math.min(previous.height, layer.height));
        }
        placements.push({ x: layer.offsetX, y: top - layer.height / 2, width: layer.width, height: layer.height, depth: index * MARKER_STACK_LAYER_DEPTH });
    });
    let minY = Infinity, maxY = -Infinity, minX = Infinity, maxX = -Infinity;
    for (const p of placements) {
        minY = Math.min(minY, p.y - p.height / 2);
        maxY = Math.max(maxY, p.y + p.height / 2);
        minX = Math.min(minX, p.x - p.width / 2);
        maxX = Math.max(maxX, p.x + p.width / 2);
    }
    const shift = (minY + maxY) / 2;
    for (const p of placements) p.y -= shift;
    return {
        layers: placements,
        bounds: { minX, maxX, minY: minY - shift, maxY: maxY - shift, width: maxX - minX, height: maxY - minY },
    };
}

export interface Vec3Like { x: number; y: number; z: number }

/**
 * Ray against a PlayCanvas plane primitive (local XZ square, -0.5..0.5, normal +Y) given the
 * inverse of its world matrix (column-major, as pc.Mat4.data). Returns the ray parameter t of
 * the hit (in the ray's own units; `dir` need not be normalised) or null. `margin` widens the
 * square, in local units, so thin layers stay easy to hit.
 */
export function rayHitsUnitPlane(origin: Vec3Like, dir: Vec3Like, inverseWorld: ArrayLike<number>, margin = 0): number | null {
    const m = inverseWorld;
    const ox = m[0] * origin.x + m[4] * origin.y + m[8] * origin.z + m[12];
    const oy = m[1] * origin.x + m[5] * origin.y + m[9] * origin.z + m[13];
    const oz = m[2] * origin.x + m[6] * origin.y + m[10] * origin.z + m[14];
    const dx = m[0] * dir.x + m[4] * dir.y + m[8] * dir.z;
    const dy = m[1] * dir.x + m[5] * dir.y + m[9] * dir.z;
    const dz = m[2] * dir.x + m[6] * dir.y + m[10] * dir.z;
    if (!Number.isFinite(dy) || Math.abs(dy) < 1e-12) return null;
    const t = -oy / dy;
    if (!Number.isFinite(t) || t < 0) return null;
    const limit = 0.5 + Math.max(0, margin);
    const hx = ox + dx * t;
    const hz = oz + dz * t;
    return Math.abs(hx) <= limit && Math.abs(hz) <= limit ? t : null;
}

/**
 * Distance along a line from the stack's pivot to where it leaves the stack's bounds, in
 * world units. `dirRight` / `dirUp` are the components of the line's unit direction along the
 * marker's right and up axes (the out-of-plane part only lengthens the result); `scale` is
 * the marker's right / up scale. A leader line starts here, so it leaves from the bottom edge
 * of the sign when its target is below, from a side when the target is beside it, and never
 * from inside the stack. A line straight out of the plane returns 0.
 */
export function markerStackEdgeDistance(
    bounds: MarkerStackBounds,
    scale: { x: number; y: number },
    dirRight: number,
    dirUp: number,
): number {
    const sx = Math.abs(finiteOr(scale.x, 1));
    const sy = Math.abs(finiteOr(scale.y, 1));
    const right = Math.max(0, (dirRight >= 0 ? bounds.maxX : -bounds.minX) * sx);
    const up = Math.max(0, (dirUp >= 0 ? bounds.maxY : -bounds.minY) * sy);
    const ax = Math.abs(finiteOr(dirRight, 0));
    const ay = Math.abs(finiteOr(dirUp, 0));
    if (ax < 1e-9 && ay < 1e-9) return 0;
    return Math.min(ax > 1e-9 ? right / ax : Infinity, ay > 1e-9 ? up / ay : Infinity);
}

/**
 * Copy a stack for a duplicated marker: fresh layer ids, no upload filenames (those belong to the
 * source). A video layer whose name was the only sign of a WebM (a blob: URL) keeps its
 * transparency as an explicit webmHasAlpha.
 */
export function cloneMarkerStackLayers(layers: readonly MarkerLayer[] | null | undefined, makeId: () => string): MarkerLayer[] | undefined {
    if (!Array.isArray(layers)) return undefined;
    return layers.map((layer) => {
        const copy: MarkerLayer = { ...layer, id: makeId() };
        if (layer?.kind === 'video') {
            const webmHasAlpha = webmHasAlphaWithoutName(layer.url, layer.name, layer.webmHasAlpha);
            if (webmHasAlpha !== layer.webmHasAlpha) copy.webmHasAlpha = webmHasAlpha;
        }
        delete copy.name;
        delete copy.videoBackupName;
        delete copy.iosMainVideoName;
        delete copy.alphaMaskVideoName;
        return copy;
    });
}
